import { Button, Dropdown, Form } from 'react-bootstrap';
import { FaChevronDown } from 'react-icons/fa6';

const ResponderDropdownSelector = ({
  options = [],
  selectedOption = [],
  setSelected = () => {},
  multiple = false,
  haveCheckboxes = false,
  reorderable = false,
  onChange,
  show,
  onToggle,
  triggerStyle,
  contentStyle,
  closeOnSelect = false,
  dropDirection = 'down',
  showPinnedTopCheckbox = false,
  pinnedTopCheckboxLabel = 'Supervisor',
  pinnedTopCheckboxChecked = false,
  onPinnedTopCheckboxChange = () => {},
}) => {
  const isSelected = (index) =>
    multiple ? selectedOption.includes(index) : selectedOption === index;

  const reorderSelectedOption = (fromPosition, toPosition) => {
    if (!multiple || !Array.isArray(selectedOption)) return;
    if (fromPosition === toPosition) return;
    if (fromPosition < 0 || toPosition < 0) return;
    if (fromPosition >= selectedOption.length || toPosition >= selectedOption.length) return;
    const next = [...selectedOption];
    const [moved] = next.splice(fromPosition, 1);
    next.splice(toPosition, 0, moved);
    setSelected(next);
  };

  const handlePriorityDrop = (toPosition, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const rawFromPosition = event?.dataTransfer?.getData('text/plain');
    const fromPosition = Number(rawFromPosition);
    if (!Number.isFinite(fromPosition)) return;
    reorderSelectedOption(fromPosition, toPosition);
  };

  const handleSelect = (index, event, item) => {
    if (typeof onChange === 'function') {
      onChange(event, item, index);
    }

    if (!multiple) {
      setSelected(index);
      return;
    }

    if (selectedOption.includes(index)) {
      setSelected(selectedOption.filter((optionIndex) => optionIndex !== index));
      return;
    }

    setSelected([...selectedOption, index]);
  };

  const getDropdownLabel = () => {
    if (!multiple) return options[selectedOption];
    if (selectedOption.length === 0) return 'Select Options';
    return selectedOption.map((index) => options[index]).join(', ');
  };

  const autoCloseMode = closeOnSelect ? 'inside' : 'outside';
  const handlePinnedTopToggle = (nextChecked, event) => {
    event?.stopPropagation?.();
    onPinnedTopCheckboxChange(Boolean(nextChecked), event);
  };

  return (
    <Dropdown
      autoClose={autoCloseMode}
      className="w-100"
      drop={dropDirection}
      show={show}
      onToggle={onToggle}
    >
      <Dropdown.Toggle
        as={Button}
        variant="primary"
        className="w-100 d-flex align-items-center rounded-2 no-caret fusion-theme-dropdown-toggle"
        style={triggerStyle}
      >
        <span className="ms-2 text-truncate max-w-lg-3xs" style={{ maxWidth: 170 }}>
          {getDropdownLabel()}
        </span>
        <FaChevronDown className="ms-auto me-2" />
      </Dropdown.Toggle>

      <Dropdown.Menu className="p-1 w-100 fusion-theme-dropdown-menu" style={{ ...contentStyle }}>
        {showPinnedTopCheckbox ? (
          <div
            className={`responder-dropdown-pinned-option ${
              pinnedTopCheckboxChecked
                ? 'responder-dropdown-pinned-option--checked'
                : 'responder-dropdown-pinned-option--unchecked'
            }`}
            role="button"
            tabIndex={0}
            onClick={(event) => handlePinnedTopToggle(!pinnedTopCheckboxChecked, event)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handlePinnedTopToggle(!pinnedTopCheckboxChecked, event);
              }
            }}
          >
            <Form.Check
              className="flex-grow-1 pe-2"
              type="checkbox"
              label={pinnedTopCheckboxLabel}
              checked={!!pinnedTopCheckboxChecked}
              onClick={(event) => {
                event?.stopPropagation?.();
              }}
              onChange={(event) => {
                handlePinnedTopToggle(event.target.checked, event);
              }}
            />
          </div>
        ) : null}
        {multiple && reorderable && selectedOption.length > 0 ? (
          <div className="responder-dropdown-priority-section">
            <div className="responder-dropdown-priority-title">Priority (drag to reorder)</div>
            <div className="responder-dropdown-priority-list">
              {selectedOption.map((selectedIndex, position) => (
                <div
                  key={`selected-priority-${selectedIndex}-${position}`}
                  className="responder-dropdown-priority-item responder-interactive-item"
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/plain', `${position}`);
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    event.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(event) => handlePriorityDrop(position, event)}
                >
                  <span className="responder-dropdown-priority-handle" aria-hidden>
                    ::
                  </span>
                  <span className="responder-dropdown-priority-rank">{position + 1}.</span>
                  <span className="responder-dropdown-priority-label">
                    {options[selectedIndex] ?? `Option ${selectedIndex}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {multiple && reorderable ? (
          <div className="responder-dropdown-options-title">Available duty selections</div>
        ) : null}
        {options.map((item, index) => (
          <Dropdown.Item
            className={`rounded-2 d-flex align-items-center responder-interactive-item fusion-theme-dropdown-item ${
              isSelected(index) ? 'is-selected' : ''
            }`}
            key={index}
            active={isSelected(index)}
            onClick={(event) => handleSelect(index, event, item)}
          >
            {haveCheckboxes || multiple ? (
              <Form.Check
                className="flex-grow-1 pe-2"
                type="checkbox"
                label={item}
                disabled
                checked={isSelected(index)}
              />
            ) : (
              <span className="flex-grow-1 pe-2">{item}</span>
            )}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ResponderDropdownSelector;
